import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useModulesBackupStore, useRouterStore } from "../../../stores";
import { message } from "antd";
import {
  CloseCircleOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  LockOutlined,
  CameraOutlined,
} from "@ant-design/icons";
import { getAllModulesByProfileId } from "../../../apis";
import {
  DialogCloseSession,
  ChangePasswordModal,
  ChangeAvatarModal,
} from "../../../../root/components/dialog";
import "../../css/user-avatar.css";

// ── Avatar con fallback a iniciales si la imagen falla ───────────────────────
const AvatarDisplay = ({
  src,
  name,
  className,
  style,
}: {
  src?: string | null;
  name?: string | null;
  className?: string;
  style?: React.CSSProperties;
}) => {
  const [imgError, setImgError] = React.useState(false);
  const showImg = src && !imgError;
  return showImg ? (
    <img
      src={src}
      alt={name ?? ""}
      className={className}
      style={style}
      onError={() => setImgError(true)}
    />
  ) : (
    <div
      className={className}
      style={{
        ...style,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 15,
        fontWeight: 700,
        letterSpacing: "0.02em",
      }}
    >
      {getInitials(name)}
    </div>
  );
};
const getInitials = (name?: string | null): string => {
  if (!name) return "U";
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
};

export const UserAvatar = () => {
  const navigate = useNavigate();
  const firstName = useRouterStore((s) => s.firstName);
  const avatar = useRouterStore((s) => s.avatar);

  const [openSession, setOpenSession] = useState(false);
  const [openPassword, setOpenPassword] = useState(false);
  const [openAvatar, setOpenAvatar] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [swVersion, setSwVersion] = useState("v1.0.0");


  useEffect(() => {
    setSwVersion(process.env.REACT_APP_VERSION ?? "v1.0.0");
  }, []);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const closeDropdown = () => setDropdownVisible(false);

  const handleReloadModules = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    closeDropdown();
    try {
      await reloadModules();
    } catch {}
  }, []);

  // ── FIX: abrir modal de sesión sin re-render pesado ─────────────────────────
  const handleOpenSession = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    closeDropdown();
    // requestAnimationFrame garantiza que el dropdown cierre antes de montar el modal
    requestAnimationFrame(() => setOpenSession(true));
  }, []);

  const handleCloseSession = useCallback(() => setOpenSession(false), []);

  const handleOpenPassword = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    closeDropdown();
    requestAnimationFrame(() => setOpenPassword(true));
  }, []);

  const handleOpenAvatar = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    closeDropdown();
    requestAnimationFrame(() => setOpenAvatar(true));
  }, []);


  // ── Menú personalizado ──────────────────────────────────────────────────────
  const DropdownMenu = (
    <div className="ua-menu">
      {/* Perfil */}
      <div
        className="ua-profile"
        onClick={() => {
          navigate("/user/information");
          closeDropdown();
        }}
      >
        <div className="ua-profile-avatar">
          <AvatarDisplay
            src={avatar}
            name={firstName}
            style={{
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              objectFit: "cover" as const,
            }}
          />
        </div>
        <div className="ua-profile-info">
          <div className="ua-profile-name">{firstName ?? "Usuario"}</div>
          <div className="ua-profile-sub">Ver perfil →</div>
        </div>
      </div>

      <div className="ua-divider" />

      {/* Recargar módulos */}
      <div className="ua-item teal" onClick={handleReloadModules}>
        <div className="ua-item-icon">
          <ReloadOutlined />
        </div>
        <span className="ua-item-label">Recargar módulos</span>
      </div>

      <div className="ua-divider" />

      {/* Cambiar avatar */}
      <div className="ua-item queue" onClick={handleOpenAvatar}>
        <div className="ua-item-icon">
          <CameraOutlined />
        </div>
        <span className="ua-item-label">Cambiar avatar</span>
      </div>

      <div className="ua-divider" />

      {/* Cambiar contraseña */}
      <div className="ua-item sync" onClick={handleOpenPassword}>
        <div className="ua-item-icon">
          <LockOutlined />
        </div>
        <span className="ua-item-label">Cambiar contraseña</span>
      </div>


      <div className="ua-divider" />

      {/* Cerrar sesión */}
      <div className="ua-item logout" onClick={handleOpenSession}>
        <div className="ua-item-icon">
          <CloseCircleOutlined />
        </div>
        <span className="ua-item-label">Cerrar sesión</span>
      </div>

      <div className="ua-divider" />

      {/* Versión */}
      <div className="ua-item disabled">
        <div className="ua-item-icon" style={{ color: "#999" }}>
          <InfoCircleOutlined />
        </div>
        <span className="ua-version-text">Versión {swVersion}</span>
        <div className={`ua-status-dot online`} />
      </div>
    </div>
  );

  return (
    <div>
      {/* Modal cerrar sesión */}
      <DialogCloseSession open={openSession} handleClose={handleCloseSession} />

      {/* Modales de cuenta */}
      <ChangeAvatarModal
        open={openAvatar}
        onClose={() => setOpenAvatar(false)}
      />
      <ChangePasswordModal
        open={openPassword}
        onClose={() => setOpenPassword(false)}
      />

      {/* Dropdown con menú personalizado */}
      <div style={{ position: "relative" }}>
       
          <div
            onClick={() => setDropdownVisible((v) => !v)}
            style={{ cursor: "pointer" }}
          >
            <AvatarDisplay
              src={avatar}
              name={firstName}
              className="avatar-element"
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                objectFit: "cover" as const,
              }}
            />
          </div>

        {/* Menú custom */}
        {dropdownVisible && (
          <>
            {/* Overlay para cerrar */}
            <div
              style={{ position: "fixed", inset: 0, zIndex: 999 }}
              onClick={closeDropdown}
            />
            <div
              style={{
                position: "fixed",
                top: "57px",
                right: "8px",
                zIndex: 1000,
              }}
            >
              {DropdownMenu}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── Recargar módulos ──────────────────────────────────────────────────────────
export const reloadModules = async () => {
  const { setModules } = useRouterStore.getState();
  const { modulesBackup, setModulesBackup } = useModulesBackupStore.getState();

  if (navigator.onLine) {
    try {
      const tokenData = JSON.parse(
        localStorage.getItem(process.env.REACT_APP_TOKEN!) || "{}",
      );
      const { token, user } = tokenData;
      if (!token || !user?.profileId) throw new Error("Sin token o profileId");

      const { data } = await getAllModulesByProfileId(user.profileId);
      if (!data) throw new Error("Sin módulos");

      setModules(data);
      setModulesBackup(data);
      message.success("Módulos recargados");
      return data;
    } catch (err: any) {
      if (!navigator.onLine || err?.response?.status === 401) {
        if (modulesBackup.length) {
          setModules(modulesBackup);
          message.warning("Módulos desde backup");
          return modulesBackup;
        }
        message.error("Sin conexión y sin backup");
        throw err;
      }
      message.error("Error al recargar módulos");
      throw err;
    }
  } else {
    if (modulesBackup.length) {
      setModules(modulesBackup);
      message.warning("Módulos desde backup");
      return modulesBackup;
    }
    message.error("Sin conexión y sin backup");
    throw new Error("Sin módulos disponibles");
  }
};
